import type {
  CSSProperties,
  ReactNode,
  MouseEvent,
  JSXElementConstructor,
  ReactElement,
} from "react";
import type {
  SorterResult,
  TableColumnProps,
  PaginationProps,
  TableColumnType,
  FormInstance,
} from "@gw/web-basic-components";
import type { IRelationModalProps } from "./components/relationModal/dto";
import type { FormGroupData, ButtonConfig } from "../dynamicForm/dto";
import type { ILeadingModalProps } from "./components/leadingInModal/dto";
import type { FormModalConfig, ValuesChangeParams } from "../formModal/dto";
import type { ColSearchParam } from "./components/colSearch/dto";

export type {
  TableColumnProps,
  FormModalConfig,
  ILeadingModalProps,
  IRelationModalProps,
  MouseEvent,
  PaginationProps,
  ColSearchParam,
};

export type HandleTableChange = (
  _pagination: PaginationProps,
  _filters: Record<string, TableColumnType<any>["filteredValue"]>,
  _sorter: SorterResult<any>
) => void;

export type GetList = (
  _pagination: PaginationProps,
  _sorter?: SortParams[],
  _searchParam?: { key: string; value: string },
  _filters?: Record<string, any | null>,
  _extraParam?: Record<string, any>
) => Promise<void>;

// 搜索框的配置
export interface TableSearchConfig {
  placeholder?: string;
  typeOptions?: { value: string; label: string }[];
  apiName?: string;
  /**
   * 指定备选项下拉菜单接口名
   */
  fetchName?: string;
  /**
   * 额外需要的接口参数
   * 当使用key、value关键字时会与内置的key、value进行拼接
   */
  extraParam?: Record<string, any>;
  /**
   * 默认分页,为ture时后端不分页
   */
  notPage?: boolean;
  /**
   * 是否过滤已关联项
   * 以值作为主键进行过滤
   */
  isFilter?: string;
  /**
   * 排序字段
   */
  orderBy?: string;
  /**
   * 自定义搜索区域
   */
  render?: (
    param: Record<string, any>
  ) => ReactElement<any, string | JSXElementConstructor<any>>;
  /**
   * 是否隐藏左侧搜索框
   */
  searchDisplay?: boolean;
}
/**
 * 筛选类型
 * Select 作为前端特有，传给后端前需要做映射
 */
export type MatchType = "Equals" | "Like" | "In" | "Between" | "Select";
// 标签栏配置
export interface TableTabsConfig {
  key: string;
  value: any;
  matchType: MatchType;
}

//提示语配置
export interface TableTipsConfig {
  delete?: string;
  batchDelete?: string;
  add?: string;
  update?: string;
  unSelect?: string;
}

/**
 * 内置行内操作
 */
type TableRawIconInner = "delete" | "edit" | "bind" | "show";

/**
 * 行内操作 icon 提示（内置）
 */
export type TableRawIconTips = {
  [key: string]: string;
};

/**
 * 自定义图片 svg 链接
 */
export type TableRawIconSvgs = {
  [opt in TableRawIconInner]?: any;
};

// type FormGroupkey = "basic" | "info";

//表单编辑框配置
export interface TableFormModalConfig<T> {
  /**
   * 新增弹窗-标题
   */
  addTitle?: string;
  /**
   * 编辑弹窗-标题
   */
  editTitle?: string;
  /**
   * 表单项配置
   */
  formGroupData?: FormGroupData;
  /**
   * 表单章节名
   */
  formGroupName?: Record<string, string>;
  /**
   * 底部按钮
   */
  extendsButton?: ButtonConfig[];
  /**
   * 弹窗打开之后-表单项生成前的回调
   */
  handleFormGroupData?: (
    event: FormGroupData,
    record: T,
    isAdd?: boolean
  ) => any;
  /**
   * 底部按钮处理
   */
  handleButton?: (originalButton: ButtonConfig[], isAdd?: boolean) => any;
  /**
   * 编辑请求结果返回之后-回调
   */
  afterUpdate?: (event, record) => Promise<any>;
  /**
   * 表单值变更时回调
   */
  onValuesChange?: (params: ValuesChangeParams) => void;
  /**
   * todo 测试中
   */
  updateForm?: (form: FormInstance) => void;
}

/**
 * 数据转换逻辑
 */
export interface TableDataTransformConfig<T = any> {
  /**
   * 获取列表数据后-展示列表数据前的数据转换逻辑
   */
  beforeList?: (event: IResDataList<T>) => any;
  /**
   * 获取详情数据后-弹窗打开前的数据转换逻辑
   */
  beforeEdit?: (event: T) => any;
  /**
   * 编辑数据后-提交数据前的数据转换逻辑
   */
  afterEdit?: (event: Record<string, any>, record: T) => any;
}

export interface ColumnConfig extends TableColumnProps<any> {
  title: string | ((props: Record<string, any>) => ReactNode);
  dataIndex: string;
  key: string;
  timeformat?: boolean;
  render?: (
    text: string | number | boolean | Record<string, any>[],
    record: Record<string, any>,
    index: number
  ) => any;
  width?: string | number;
  /**
   * 后端列表排序，只支持单列排序
   */
  sorter?:
    | boolean
    | ((a: Record<string, any>, b: Record<string, any>) => number);
  /**
   * 开启 search，使用 colSearch 组件
   */
  searchable?: boolean;
  /*
   * 开启 sort 属性，即将删除 sorter 属性
   */
  sortable?: boolean;
  /**
   * 显示下一次排序的提示
   */
  showSorterTooltip?: boolean;
  /**
   * 筛选类型
   * 此为自定义类型，直接映射给接口
   */
  filterType?: MatchType;
  /**
   * 自定义筛选菜单
   */
  filterDropdown?: TableColumnType<any>["filterDropdown"];
  /**
   * 自定义筛选菜单
   */
  renderFilterDropdown?: () => ReactElement<
    any,
    string | JSXElementConstructor<any>
  >;
  /**
   * 自定义筛选菜单样式
   */
  filterDropdownClassName?: string;
  /**
   * 自定义筛选图标
   */
  filterIcon?: ReactNode | ((filtered: boolean) => ReactNode);
  /**
   * 是否搜索备选项
   */
  filterSearch?: boolean;
  /**
   * 手动配置备选项
   */
  options?: { label: string; value: any; disbaled?: boolean }[];

  requestConfig?: {
    /**
     * 列筛选自定义接口指向,默认与tableName相同
     */
    apiName?: string;
    /**
     * 列筛选自定义接口名，默认用altnatives
     */
    fetchName?: string;

    optionLabel?: string;
    optionValue?: string;
    changeType?: string;
    changeColName?: string;
  };
}
/**
 * 丰富的操作列配置
 * 1.布尔类型 true-启用 false-禁用
 * 2.字符串数组 默认启用 并将所有字符串映射record,加入接口传参
 * 3.function 默认启用 根据返回结果决定某行是否启用指定icon
 */
type rawConfigType =
  | boolean
  | string[]
  | ((record: Record<string, any>) => boolean | string[]);

export interface TableRawOptConfig {
  edit?: rawConfigType;
  bind?: rawConfigType;
  delete?: rawConfigType;
  [propsName: string]: rawConfigType;
}

/**
 * 表单操作配置
 * @param add 新增
 * @param delete 批量删除
 * @param bind 关联
 * @param leadingOut 批量导出
 * @param leadingIn 批量导入
 */
export interface TableOperationBarConfig {
  add?: boolean;
  delete?: boolean;
  bind?: boolean;
  leadingOut?: boolean;
  leadingIn?: boolean;
  [propsName: string]: boolean;
}

export interface PaginationConfig {
  current: number;
  size?: number;
  pageSize?: number;
  total: number;
}

/**
 * 表格配置插件
 */
export namespace ITableConfigPlugins {
  /**
   * Table 组件刷新
   */
  export interface ITableRefresh {
    (flag?: boolean | "init"): void;
  }

  /**
   * 插件组件 props
   */
  export interface IComponentProps<T = any> {
    /**
     * 行记录
     */
    record?: T;
    /**
     * 刷新
     */
    refresh?: ITableRefresh;
    /**
     * 显示类型
     */
    type?: "row" | "operation";
  }

  /**
   * 插件 Item
   */
  export interface IPluginItem<T = any> {
    /**
     * 扩展排序
     * @deprecated 在新的实验中，更加倾向与使用key进行排序，而不是使用固定的字段
     */
    order?: number;
    /**
     * 组件
     */
    component: (props: IComponentProps<T>) => JSX.Element;
  }

  /**
   * 插件集合
   */
  export type IPlugins<T = any> = Record<string, IPluginItem<T>>;
}

/**
 * Table 组件配置
 */
export type ITableProps<T = any> = {
  /**
   * 内部样式
   */
  style?: CSSProperties;
  /**
   * 包装器类名
   */
  className?: string;
  /**
   * 表格名称
   */
  tableName?: string;
  /**
   * 某行数据的详情信息是否包含在list列表里
   */
  detailInList?: boolean;
  /**
   * 扩展参数
   */
  extraParam?: Record<string, any>;
  /**
   * 表格配置
   */
  columns?: ColumnConfig[];
  /**
   * 表格数据
   */
  dataSource?: T[];
  /**
   * 表格每行后面操作列的配置（是否显示编辑，删除等按钮）
   */
  rawOptConfig?: TableRawOptConfig;
  /**
   * 操作bar的配置（是否显示新增，删除等按钮）
   */
  operationBarConfig?: TableOperationBarConfig;
  /**
   * 搜索框的配置
   */
  searchConfig?: TableSearchConfig;
  /**
   * 标签栏配置
   */
  tabsConfig?: TableTabsConfig;
  /**
   * 提示语配置
   */
  tipsConfig?: TableTipsConfig;
  /**
   * 行内操作 icon 提示语
   */
  rawIconTitleConfig?: TableRawIconTips;
  /**
   * 行内操作（内置）svg
   */
  rawIconSvgConfig?: TableRawIconSvgs;
  /**
   * 表单编辑框配置
   */
  formModalConfig?: TableFormModalConfig<T>;
  /**
   * 数据转换逻辑
   */
  dataTransform?: TableDataTransformConfig<T>;
  /**
   * 分页配置
   */
  paginationConfig?: PaginationConfig;
  /**
   * 关联配置
   */
  relationModalConfig?: IRelationModalProps;
  /**
   * 操作栏关联配置
   */
  buildRelationModalConfig?: (record: any) => IRelationModalProps;
  /**
   * 是否需要check box
   */
  uncheckable?: boolean;
  /**
   * 批量导入配置
   */
  leadingInConfig?: ILeadingModalProps;
  /**
   * 批量导出配置
   */
  leadingOutConfig?: {
    templateDownloadPre?: string;
  };
  /**
   * 是否不分页，默认分页，为true时不分页
   */
  notPage?: boolean;
  /**
   * 尝试插件
   */
  plugins?: ITableConfigPlugins.IPlugins;
  /**
   * 标志这个表格的分页查询是post请求，此为过渡方案
   */
  isFilterTable?: boolean;
  /**
   * 暴露内部一些关键值
   */
  onGwTableChange?: (params: Record<string, any>) => void;
  /**
   * 强制刷新标志符
   * todo 设计真是太乱了
   */
  flag?: number;
  /**
   * 尺寸
   */
  size?: "small" | "middle" | "large";
};
/**
 * 后端排序参数
 */
export interface SortParams {
  column?: string;
  asc?: boolean;
}
